import { isEmpty } from 'utils/empty';
import { EachFileSelected } from 'store/models/file-selected/file-selected.interface';
import { FileSelected } from 'store/models/file-selected/FileSelected';
import { BookingIdToFoundMapI } from './bookings.interface';
import { BookingInfo } from '../BookingInfo';

export interface BookingI {
  bookingNo: string;
  fileSelected?: EachFileSelected[];
  hasErrors?: {
    bookingNoIsRequired?: boolean;
    bookingNoIsNotRightFormat?: boolean;
    bookingNoNotFound?: boolean;
    bookingIsCanceled?: boolean;
    bookingNoIsDup?: boolean;
  };
}
export declare type BookingNoErrorStatusI =
  | 'bookingNoIsRequired'
  | 'bookingNoIsNotRightFormat'
  | 'bookingNoIsDup'
  | 'bookingNoNotFound'
  | 'bookingIsCanceled';
export declare type BookingStatusI =
  | 'isSplit'
  | 'notFound'
  | 'isCancelled'
  | 'found'
  | 'isRequired'
  | 'isWrongFormat'
  | 'apiError'
  | 'isDuplicated';

export interface WorkingBookingI extends BookingI {
  isSelected?: boolean;
}

const initialBooking: BookingI = {
  bookingNo: '',
  fileSelected: [],
  hasErrors: {}, // TODO: review this
};

const initialWorkingBooking: WorkingBookingI = {
  bookingNo: '',
  fileSelected: [],
  hasErrors: {},
  isSelected: false,
};

const bookingErrorStatuses = {
  bookingNoIsRequired: 'Booking no. is required',
  bookingNoIsNotRightFormat: 'Booking no. is wrong format',
  bookingNoIsDup: 'Booking no. must be unique',

  bookingNoNotFound: 'Booking no. not found',
  bookingIsCanceled: 'Booking no. is canceled',
};

// const bookingNoRegEx = /\b(ONE[A-Za-z]\s*|)(((\w\s*){3}[A-HJ-NP-Z]{1}\s*([A-HJ-NP-Z0-9]\s*){2}(\d\s*){5}\d)|(\w\s*\w\s*\d\s*\w\s*\w\s*([A-HJ-NP-Z0-9]\s*){3}(\d\s*){3}\d))\b/g;

const bookingNoRegExFromStart = /(^\b(ONE[A-Za-z]\s*|)(((\w\s*){3}[A-HJ-NP-Z]{1}\s*([A-HJ-NP-Z0-9]\s*){2}(\d\s*){5}\d)|(\w\s*\w\s*\d\s*\w\s*\w\s*([A-HJ-NP-Z0-9]\s*){3}(\d\s*){3}\d))\b)((?:,)\b(ONE[A-Za-z]\s*|)(((\w\s*){3}[A-HJ-NP-Z]{1}\s*([A-HJ-NP-Z0-9]\s*){2}(\d\s*){5}\d)|(\w\s*\w\s*\d\s*\w\s*\w\s*([A-HJ-NP-Z0-9]\s*){3}(\d\s*){3}\d))\b)?/g;

// example correct booking no JKTV40373900

class Booking {
  booking: WorkingBookingI;
  passFoundNoCancel: boolean;
  constructor(booking = initialWorkingBooking) {
    this.booking = booking;
    this.trimmedBookingNo();
    this.passFoundNoCancel = true;
  }
  get hasErrors() {
    return this.booking.hasErrors;
  }
  get bookingNo() {
    return this.booking.bookingNo;
  }
  doesHaveErrors(): boolean {
    if (!this.hasErrors) return false;
    const values = Object.values(this.hasErrors);
    if (!values.length) return false;
    return values.some((v) => !!v);
  }
  get isSelected() {
    return this.booking.isSelected;
  }
  markIsSelected(
    val: boolean,
    returnModel = false,
  ): Booking | BookingI {
    this.booking.isSelected = val;
    return returnModel ? this : this.booking;
  }
  trimmedBookingNo() {
    this.booking = {
      ...this.booking,
      bookingNo: (this.booking.bookingNo || '').replace(/\s/g, ''),
    };
    return this.booking;
  }
  isBookingNoEmpty() {
    return isEmpty(this.booking.bookingNo);
  }

  static isBookingNo(value: string): boolean {
    if (!value) return false;
    return !!value.match(bookingNoRegExFromStart);
  }

  static replaceONEY(v: string): string {
    return v.replace(/ONE[A-Za-z]/, '');
  }
  /**
   * @returns isMatched - true means correct format
   */
  isBookingNoRightFormat() {
    const isMatched = this.booking.bookingNo.match(
      bookingNoRegExFromStart,
    );
    return isMatched;
  }

  static getErrorStatus(booking: BookingI) {
    const errorKey = Booking.getErrorStatusKey(booking);
    if (errorKey) {
      return bookingErrorStatuses[errorKey];
    } else {
      return null;
    }
  }
  getErrorStatus(): string | null {
    return Booking.getErrorStatus(this.booking);
  }

  static getErrorStatusKey(booking: BookingI) {
    return [
      'bookingNoIsRequired',
      'bookingNoIsNotRightFormat',
      'bookingNoIsDup',
      'bookingNoNotFound',
      'bookingIsCanceled',
    ].find((k) => booking.hasErrors && booking.hasErrors[k]);
  }
  getErrorStatusKey(): string | undefined {
    return Booking.getErrorStatusKey(this.booking);
  }

  static bookingStatusToStatusText(
    status: BookingStatusI | '',
  ): string {
    if (!status) return '';
    const data = {
      isSplit: 'Booking no. is split',
      notFound: 'Booking no. not found',
      isCancelled: 'Booking no. is canceled',
      found: '',
      isRequired: 'Booking no. is required',
      isWrongFormat: 'Booking no. is wrong format',
      isDuplicated: 'Booking no. must be unique',
      apiError: 'API error',
    };
    return data[status];
  }

  getBookingStatus(bookingInfoM?: BookingInfo): BookingStatusI {
    const errorKey = this.getErrorStatusKey();
    const isSplit = bookingInfoM?.isSplit;
    const apiError = bookingInfoM?.failedFromAnyApi;
    const errorKeyToStatus: { [errorKey: string]: BookingStatusI } = {
      bookingNoIsRequired: 'isRequired',
      bookingNoIsNotRightFormat: 'isWrongFormat',
      bookingNoIsDup: 'isDuplicated',
      bookingIsCanceled: 'isCancelled',
      bookingNoNotFound: 'notFound',
    };
    if (apiError) return 'apiError';
    else if (errorKey && errorKey === 'bookingNoIsDup')
      return errorKeyToStatus.bookingNoIsDup;
    else if (isSplit) return 'isSplit';
    return errorKey ? errorKeyToStatus[errorKey] : 'found';
  }

  getBookingStatusAndStatusText(
    bookingInfoM?: BookingInfo,
  ): [BookingStatusI, string] {
    const status = this.getBookingStatus(bookingInfoM);
    const statusText = Booking.bookingStatusToStatusText(status);
    return [status, statusText];
  }

  getRelavantFoundMap(
    foundMap: BookingIdToFoundMapI,
  ): BookingIdToFoundMapI {
    return Object.keys(foundMap)
      .filter((no) => this.bookingNo === no)
      .reduce((acc, no) => ({ ...acc, [no]: foundMap[no] }), {});
  }

  validateFoundAndNotCanceled(
    foundMap: BookingIdToFoundMapI,
  ): Booking {
    let val = foundMap[this.bookingNo];
    if (val && val.found && val.notCanceled) {
      // pass -> clear not found and canceled error
      this.clearErrors(['bookingIsCanceled', 'bookingNoNotFound']);
    } else {
      this.passFoundNoCancel = false;
      // failed
      if (!val || !val.found) {
        // set not found error
        this.markHasError('bookingNoNotFound');
      } else {
        this.clearError('bookingNoNotFound');
      }
      if (val && !val.notCanceled) {
        // set cancel error
        this.markHasError('bookingIsCanceled');
      } else {
        this.clearError('bookingIsCanceled');
      }
    }
    return this;
  }

  /**
   * validate if empty or not right format
   * @param skipValidateEmpty
   */
  validateBookingNoAndAddErrorStatus(
    skipValidateEmpty = false,
    foundMap?: BookingIdToFoundMapI, // TODO: check if has to pass the relavant ones only or not
  ) {
    let isEmptyV = this.isBookingNoEmpty();
    const isNotRightFormat = !this.isBookingNoRightFormat();

    if (!skipValidateEmpty) {
      if (isEmptyV) {
        this.markHasError('bookingNoIsRequired');
      } else {
        this.clearError('bookingNoIsRequired');
      }
    }

    if (!isEmptyV && isNotRightFormat) {
      this.markHasError('bookingNoIsNotRightFormat');
    } else {
      this.clearError('bookingNoIsNotRightFormat');
    }

    if (!isEmptyV && !isNotRightFormat && foundMap) {
      this.validateFoundAndNotCanceled(foundMap);
    }
    return this;
  }

  markHasError(status: BookingNoErrorStatusI) {
    const b = this.booking;
    this.booking = {
      ...b,
      hasErrors: { ...b.hasErrors, [status]: true },
    };
    return new Booking(this.booking);
  }
  clearError(statusToClear: BookingNoErrorStatusI) {
    const b = this.booking;
    this.booking = {
      ...b,
      hasErrors: {
        ...b.hasErrors,
        [statusToClear]: false,
      },
    };
    return new Booking(this.booking);
  }

  clearAllErrors() {
    const b = this.booking;
    this.booking = {
      ...b,
      hasErrors: {},
    };
    return new Booking(this.booking);
  }

  clearErrors(statusesToClear: BookingNoErrorStatusI[]) {
    const b = this.booking;
    this.booking = {
      ...b,
      hasErrors: {
        ...b.hasErrors,
        ...statusesToClear.reduce(
          (acc, status) => ({ ...acc, [status]: false }),
          {},
        ),
      },
    };
    return new Booking(this.booking);
  }

  updateBookingNo(value: string) {
    this.booking.bookingNo = value;
    return new Booking(this.booking);
  }

  removeFiles(partIds: string[]) {
    this.booking = {
      ...this.booking,
      fileSelected: FileSelected.removeFiles(
        this.booking.fileSelected,
        partIds,
      ),
    };

    return new Booking(this.booking);
  }
}

export default Booking;

export {
  initialBooking,
  initialWorkingBooking,
  // bookingNoRegEx,
  bookingErrorStatuses,
};
